import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowLeft, Loader2, CheckCircle, Hash } from 'lucide-react';
import apiService from '../services/api';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOTP] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    // Redirect to forgot password if email is not provided
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setError(isRTL ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters');
      return;
    }

    // Validate OTP format
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setError(isRTL ? 'رمز التحقق يجب أن يكون 6 أرقام' : 'OTP must be 6 digits');
      return;
    }

    setLoading(true);

    try {
      await apiService.resetPassword(email, otp, newPassword);
      setSuccess(true);
      // Navigate to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message;
      if (errorMessage?.includes('expired')) {
        setError(isRTL ? 'انتهت صلاحية رمز التحقق' : 'OTP has expired');
      } else if (errorMessage?.includes('Invalid')) {
        setError(isRTL ? 'رمز التحقق غير صحيح' : 'Invalid OTP');
      } else {
        setError(errorMessage || t('auth.resetPasswordError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-emerald-50 relative overflow-hidden p-4">
      {/* Animated Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute top-20 right-20 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-20 left-40 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-md relative z-10 animate-fadeIn">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 px-8 py-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-800/20 rounded-full -ml-12 -mb-12"></div>

            <div className="relative text-center">
              <h1 className="text-3xl font-black text-white mb-2">
                {isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
              </h1>
              <p className="text-brand-50 text-sm">
                {isRTL
                  ? 'أدخل رمز التحقق وكلمة المرور الجديدة'
                  : 'Enter the verification code and new password'}
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-8">
            {success ? (
              <div className="text-center animate-fadeIn">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'تم بنجاح!' : 'Success!'}
                </h3>
                <p className="text-gray-600 text-sm" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL
                    ? 'تم إعادة تعيين كلمة المرور بنجاح'
                    : 'Password has been reset successfully'}
                </p>
                <p className="text-gray-500 text-xs mt-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {isRTL ? 'جاري التحويل لتسجيل الدخول...' : 'Redirecting to login...'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-fadeIn">
                    <p className="text-red-800 font-medium text-sm">{error}</p>
                  </div>
                )}

                {/* Info Message */}
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-4">
                  <p className="text-blue-800 text-sm" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL
                      ? `تم إرسال رمز التحقق إلى ${email}`
                      : `Verification code sent to ${email}`}
                  </p>
                  <p className="text-blue-600 text-xs mt-1" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'صالح لمدة 15 دقيقة' : 'Valid for 15 minutes'}
                  </p>
                </div>

                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'رمز التحقق' : 'Verification Code'}
                  </label>
                  <div className="relative">
                    <Hash className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                    <input
                      type="text"
                      placeholder={isRTL ? 'أدخل رمز التحقق المكون من 6 أرقام' : 'Enter 6-digit code'}
                      value={otp}
                      onChange={(e) => setOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      disabled={loading}
                      maxLength={6}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-mono text-lg tracking-widest text-center disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                  </div>
                </div>

                {/* New Password Input */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'كلمة المرور الجديدة' : 'New Password'}
                  </label>
                  <div className="relative">
                    <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                    <input
                      type="password"
                      placeholder={isRTL ? 'أدخل كلمة المرور الجديدة' : 'Enter new password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={loading}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                  </div>
                </div>

                {/* Confirm Password Input */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                  </label>
                  <div className="relative">
                    <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-4' : 'left-4'}`} />
                    <input
                      type="password"
                      placeholder={isRTL ? 'أعد إدخال كلمة المرور' : 'Re-enter password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={`w-full ${isRTL ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    <span>{isRTL ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}</span>
                  )}
                </button>

                {/* Back to Forgot Password */}
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="w-full py-3 text-gray-600 hover:text-brand-600 font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                  <span>{isRTL ? 'طلب رمز جديد' : 'Request New Code'}</span>
                </button>
              </form>
            )}
          </div>

          {/* Bottom Accent */}
          <div className="h-2 bg-gradient-to-r from-brand-500 via-brand-600 to-emerald-600"></div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
